// React Dependencies
import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import MaterialTable from 'material-table';
import { toast } from 'react-toastify';
import { Chip } from '@material-ui/core';

// Internal Dependencies
import { updateModalContent, toggleModal } from '../../../actions/modal';
import MText from '../../common/MText';
import Colours from '../../../styles/colours';
import apiService from '../../../services.js/apiService';
import { IsArray, IsEmpty } from '../../../common/checks';
import GroupModal from './groupModal';
import OverLoading from '../../global/OverLoading';

const TagGroupTable = forwardRef((props, ref) => {
  const table = useRef(null);
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [editGroup, setEditGroup] = useState(null);

  // States for expandable chips per row
  const [expandedRows, setExpandedRows] = useState({});

  const fetchGroups = async () => {
    try {
      setLoading(true);
      const result = await apiService.getPopularTagGrouping();
      setTableData(IsArray(result) ? result : []);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    refreshTable() {
      fetchGroups();
    },
  }));

  useEffect(() => {
    fetchGroups();
  }, []);

  const handleDelete = async (group) => {
    props.updateModalContent({
      title: (
        <MText text="Remove Confirmation" size={16} bold />
      ),
      content: (
        <MText
          text={(
            <>
              Delete group
              {' '}
              <strong>{group.name}</strong>
              ?
            </>
          )}
          size={14}
        />
      ),
      cancelText:   'Cancel',
      actionButton: {
        actionText: 'Delete',
        actionType: 'danger',
        action:     async () => {
          try {
            setLoading(true);
            const newData = tableData.filter((g) => g.id !== group.id);
            await apiService.updatePopularTagGrouping(newData);
            toast.success(`"${group.name}" group deleted`);
            setTableData(newData);
          } catch (err) {
            toast.error(`Failed to delete group: ${err.message}`);
          } finally {
            setLoading(false);
          }
        },
      },
      centered:         true,
      closeAfterAction: true,
    });
    props.toggleModal();
  };

  const handleEdit = (group) => {
    setEditGroup(group);
  };

  const handleCloseEdit = () => {
    setEditGroup(null);
    fetchGroups(); // refresh after edit
  };

  const toggleExpand = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const renderTags = (row) => {
    const tags = row.tags || [];
    if (IsEmpty(tags)) {
      return <MText text="-" />;
    }

    const isExpanded = !!expandedRows[row.id];
    const maxVisible = 5;

    const visibleTags = isExpanded ? tags : tags.slice(0, maxVisible);
    const hiddenCount = tags.length - maxVisible;

    const tagElements = visibleTags.map((tag) => (
      <Chip
        key={`${row.id}-tag-${tag}`}
        label={tag}
        size="small"
        style={{ backgroundColor: Colours.Purple, color: Colours.White, fontWeight: 'bold' }}
      />
    ));

    if (!isExpanded && hiddenCount > 0) {
      tagElements.push(
        <Chip
          key={`${row.id}-show-more`}
          label="Show More"
          size="small"
          onClick={() => toggleExpand(row.id)}
          style={{ cursor: 'pointer', backgroundColor: Colours.Gray3 }}
        />,
      );
    } else if (isExpanded && hiddenCount > 0) {
      tagElements.push(
        <Chip
          key={`${row.id}-show-less`}
          label="Show Less"
          size="small"
          onClick={() => toggleExpand(row.id)}
          style={{ cursor: 'pointer', backgroundColor: Colours.Gray3 }}
        />,
      );
    }

    const chunkSize = 6;
    const chunkedElements = [];
    for (let i = 0; i < tagElements.length; i += chunkSize) {
      chunkedElements.push(tagElements.slice(i, i + chunkSize));
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {chunkedElements.map((chunk) => (
          <div key={`row-for-${chunk[0].key}`} style={{ display: 'flex', gap: '4px' }}>
            {chunk}
          </div>
        ))}
      </div>
    );
  };

  const columns = [
    {
      title:  'Group Name',
      field:  'name',
      render: (r) => (
        <MText text={r.name} />
      ),
      cellStyle:   { width: '25%' },
      headerStyle: { width: '25%' },
    },
    {
      title:  'Tags Count',
      field:  'count',
      render: (r) => (
        <MText text={IsArray(r.tags) ? r.tags.length : 0} />
      ),
      cellStyle:   { width: '15%' },
      headerStyle: { width: '15%' },
    },
    {
      title:       'Tags',
      render:      (r) => renderTags(r),
      cellStyle:   { width: '60%' },
      headerStyle: { width: '60%' },
    },
  ];

  return (
    <div>
      <MaterialTable
        tableRef={table}
        className="material-table"
        title=""
        columns={columns}
        data={tableData}
        isLoading={loading}
        actions={[
          {
            icon:    'edit',
            tooltip: 'Edit Group',
            onClick: (_, group) => handleEdit(group),
          },
          {
            icon:    'delete',
            tooltip: 'Delete Group',
            onClick: (_, group) => handleDelete(group),
          },
        ]}
        options={{
          paging:              true,
          pageSize:            20,
          pageSizeOptions:     [20, 50, 100],
          actionsColumnIndex:  -1,
          emptyRowsWhenPaging: false,
          search:              true, // Enable filtering
          sorting:             false,
          draggable:           false,
        }}
      />

      {editGroup && (
        <GroupModal onClose={handleCloseEdit} group={editGroup} />
      )}

      {loading && <OverLoading />}
    </div>
  );
});

TagGroupTable.propTypes = {
  updateModalContent: PropTypes.func.isRequired,
  toggleModal:        PropTypes.func.isRequired,
};

export default connect(null, { updateModalContent, toggleModal }, null, { forwardRef: true })(TagGroupTable);
